import { useMutation } from "@tanstack/react-query";
import { useState } from "react";

import { useAppForm } from "@/components/base/form";
import { Modal, ModalHeading } from "@/components/base/modal";
import { addToast } from "@/components/base/toast";
import { useTRPC } from "@/integrations/trpc/react";

function useNewsletterForm(onSuccess?: () => void) {
  const [isSubscribed, setIsSubscribed] = useState(false);
  const trpc = useTRPC();

  const { mutateAsync } = useMutation(
    trpc.contacts.subscribe.mutationOptions(),
  );

  const form = useAppForm({
    defaultValues: {
      email: "",
      ratedDivisions: true,
      juniorsDivisions: false,
    },
    onSubmit: async ({ value }) => {
      if (!value.ratedDivisions && !value.juniorsDivisions) {
        addToast({
          variant: "error",
          title: "Please select a mailing list",
          description: "Choose at least one mailing list to subscribe to.",
        });
        return;
      }

      try {
        await mutateAsync({
          email: value.email,
          ratedDivisions: value.ratedDivisions,
          juniorsDivisions: value.juniorsDivisions,
        });

        setIsSubscribed(true);
        onSuccess?.();

        addToast({
          variant: "success",
          title: "You're subscribed!",
          description: "Thanks for signing up for CBVA updates.",
        });
      } catch {
        addToast({
          variant: "danger",
          title: "Something went wrong",
          description: "Please try again later.",
        });
      }
    },
  });

  return { form, isSubscribed };
}

function NewsletterFormFields({
  form,
}: {
  form: ReturnType<typeof useNewsletterForm>["form"];
}) {
  return (
    <>
      <form.AppField
        name="email"
        children={(field) => (
          <field.Text
            field={field}
            label="Email"
            placeholder="you@example.com"
            type="email"
            className="[&_input]:bg-white"
          />
        )}
      />
      <div className="flex flex-col gap-2">
        <form.AppField name="ratedDivisions">
          {(field) => <field.Checkbox field={field} label="Rated Divisions" />}
        </form.AppField>
        <form.AppField name="juniorsDivisions">
          {(field) => (
            <field.Checkbox field={field} label="Juniors Divisions" />
          )}
        </form.AppField>
      </div>
      <form.AppForm>
        <form.SubmitButton
          requireChange={false}
          color="primary"
          className="w-full font-bold tracking-wide uppercase"
        >
          Sign up now
        </form.SubmitButton>
      </form.AppForm>
    </>
  );
}

export function NewsletterForm() {
  const { form, isSubscribed } = useNewsletterForm();

  if (isSubscribed) {
    return (
      <div className="bg-content-background-alt w-full px-3 py-12">
        <div className="bg-navbar-background mx-auto max-w-4xl rounded-xl px-8 py-10 text-center">
          <p className="text-navbar-foreground text-lg">
            Thanks for subscribing! Keep an eye on your inbox for CBVA updates.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-content-background-alt w-full px-3 py-12">
      <div className="bg-navbar-background mx-auto max-w-4xl rounded-xl px-8 py-10 md:px-12 md:py-12">
        <div className="flex flex-col gap-8 md:flex-row md:items-center md:gap-12">
          <div className="flex-1">
            <h2 className="text-navbar-foreground text-2xl font-extrabold tracking-tight uppercase md:text-3xl">
              Stay in the loop.
            </h2>
            <p className="text-navbar-foreground/70 mt-3 text-sm md:text-base">
              Tournament recaps, helpful tips, announcements, and so much more
              delivered to your inbox.
            </p>
          </div>
          <form
            className="[&_label]:text-navbar-foreground [&_[data-rac]]:text-navbar-foreground flex w-full shrink-0 flex-col gap-4 md:w-80"
            onSubmit={(e) => {
              e.preventDefault();
              form.handleSubmit();
            }}
          >
            <NewsletterFormFields form={form} />
          </form>
        </div>
      </div>
    </div>
  );
}

export function NewsletterModal({
  isOpen,
  onOpenChange,
}: {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
}) {
  const { form, isSubscribed } = useNewsletterForm(() => {
    setTimeout(() => onOpenChange(false), 1500);
  });

  return (
    <Modal isOpen={isOpen} onOpenChange={onOpenChange}>
      <div className="flex flex-col gap-4 p-6">
        <ModalHeading size="sm">Stay in the loop</ModalHeading>
        {isSubscribed ? (
          <p className="py-4 text-center text-sm">
            Thanks for subscribing! Keep an eye on your inbox for CBVA updates.
          </p>
        ) : (
          <>
            <p className="text-muted-foreground text-sm">
              Tournament recaps, helpful tips, announcements, and so much more
              delivered to your inbox.
            </p>
            <form
              className="flex flex-col gap-4"
              onSubmit={(e) => {
                e.preventDefault();
                form.handleSubmit();
              }}
            >
              <NewsletterFormFields form={form} />
            </form>
          </>
        )}
      </div>
    </Modal>
  );
}
